import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { CLIENT_ACTION, EMPLOYEE_ACTION } from "../redux/action";
import { NotificationContext } from "../utils/common";
import {
  buildAssignedClientIdSet,
  filterNotificationsForUser,
} from "../utils/notificationStaffScope";
import { isStaffRole } from "../utils/staffListScope";
import { collectDescendantEmployeeIds } from "../pages/reports/reportsTeamScope";

/**
 * Filters notifications for employees/managers to assigned clients (+ team leave/timesheet).
 */
export function useStaffNotificationFilter() {
  const { notifications } = useContext(NotificationContext) || {
    notifications: [],
  };
  const dispatch = useDispatch();

  const userRole = localStorage.getItem("user_role") || "";
  const userId = localStorage.getItem("user_id") || "";
  const isStaff = isStaffRole(userRole);

  const [assignedClientIds, setAssignedClientIds] = useState(null);
  const [teamEmployeeIds, setTeamEmployeeIds] = useState(null);
  const [scopeReady, setScopeReady] = useState(!isStaff);

  useEffect(() => {
    if (!isStaff || !userId) {
      setAssignedClientIds(null);
      setTeamEmployeeIds(null);
      setScopeReady(true);
      return undefined;
    }

    let cancelled = false;
    setScopeReady(false);

    (async () => {
      try {
        const clients = await dispatch(
          CLIENT_ACTION.getAllClients({ scope: "assigned" }),
        ).unwrap();

        if (cancelled) return;

        const clientIds = buildAssignedClientIdSet(clients, userId);
        setAssignedClientIds(clientIds);

        if (userRole === "manager") {
          try {
            const employees = await dispatch(
              EMPLOYEE_ACTION.getActiveEmployees(),
            ).unwrap();
            if (!cancelled) {
              const teamIds = collectDescendantEmployeeIds(
                employees || [],
                userId,
                { includeSelf: true },
              );
              setTeamEmployeeIds(teamIds);
            }
          } catch {
            if (!cancelled) {
              setTeamEmployeeIds(new Set([String(userId)]));
            }
          }
        } else {
          setTeamEmployeeIds(new Set([String(userId)]));
        }
      } catch {
        if (!cancelled) {
          setAssignedClientIds(new Set());
          setTeamEmployeeIds(new Set([String(userId)]));
        }
      } finally {
        if (!cancelled) setScopeReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isStaff, userId, userRole]);

  const visibleNotifications = useMemo(
    () =>
      filterNotificationsForUser(notifications, {
        userRole,
        userId,
        assignedClientIds,
        teamEmployeeIds,
        scopeReady,
      }),
    [
      notifications,
      userRole,
      userId,
      assignedClientIds,
      teamEmployeeIds,
      scopeReady,
    ],
  );

  return {
    visibleNotifications,
    scopeReady,
    isStaff,
  };
}
